import { useAtom } from "jotai";
import { useEffect, useState, useContext } from "react";
import { scheduleEventTypeAtom, scheduleStatusTypeAtom, installersAtom } from "../../../state/schedule/scheduleAtoms";
import { eventIcons } from "../../../state/schedule/eventIcons";
import { APIStatusContext } from "../../../providers/apiStatusProvider";
import { MessageContext } from "../../../providers/MessageProvider";
import { useAuth } from "../../../providers/AuthProvider";
import { handleSchedulingApiError } from "../../../utils/errorHandler";

import Dropdown from "../../ui/form/Dropdown";
import Input from "../../ui/form/Input";
import Checkbox from "../../ui/form/Checkbox";
import RadioGroup from "../../ui/form/RadioGroup";
import Textarea from "../../ui/form/Textarea";
import ModalWrapper from "../../ui/content/ModalWrapper";

import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
dayjs.extend(utc);
dayjs.extend(timezone);

const defaultTz = "America/Los_Angeles"
// Entra app role; the backend rejects time changes without it, this only mirrors that in the form
const RESCHEDULE_ROLE = "rescheduleAppointments";

export default function EventEdit({ eventEditParams, closeEventModal, activeEvent }) {
    const [title, setTitle] = useState("New Work Order");
    const [eventType, setEventType] = useState();
    const [eventTypes, setEventTypes] = useAtom(scheduleEventTypeAtom);
    const [eventStatus, setEventStatus] = useState();
    const [eventStatusTypes, setEventStatusTypes] = useAtom(scheduleStatusTypeAtom);
    const [installers, setInstallers] = useAtom(installersAtom);
    const [installerType, setInstallerType] = useState("1");

    const [eventSubevent, setEventSubevent] = useState();
    const [installer, setInstaller] = useState(undefined);
    const [multiInstaller, setMultiInstaller] = useState(installers.map(i => { 
        const displaySuffix = i.InstallerType.name === "Region" ? "" : " Tech";
        return {
            id: i.id, 
            display: `${i.User.firstName} ${i.User.lastName} (${i.InstallerType.name}${displaySuffix})`, 
            isChecked: false
        }
    }));
    const [jobDate, setJobDate] = useState();
    const [jobHour, setJobHour] = useState();
    const [jobMinute, setJobMinute] = useState();
    const [ampm, setAmPm] = useState("1");
    const [durationHour, setDurationHour] = useState("0");
    const [durationMinute, setDurationMinute] = useState("1");
    const [workOrderNumber, setWorkOrderNumber] = useState();
    // const [jiraTicketId, setJiraTicketId] = useState();
    const [hermesTicketId, setHermesTicketId] = useState();
    const [notes, setNotes] = useState();
    const [failedNotes, setFailedNotes] = useState();

    const { setIsLoading } = useContext(APIStatusContext);
    const { isLoading } = useState(false);
    const [showWorkOrderNumber, setShowWorkOrderNumber] = useState(true);
    const [showEventStatusType, setShowEventStatusType] = useState(false);

    const [showSubevent, setShowSubevent] = useState(false);
    const [showCustomSubevent, setShowCustomSubevent] = useState(false);
    const [customSubevent, setCustomSubevent] = useState();

    const [showFailedNotes, setShowFailedNotes] = useState(false);
    const [showServerError, setShowServerError] = useState(false);
    const [showFormError, setShowFormError] = useState(false);
    const [formErrorMessage, setFormErrorMessage] = useState("");
    
    // Real-time availability checking
    const [checkingAvailability, setCheckingAvailability] = useState(false);
    const [availabilityConflict, setAvailabilityConflict] = useState(null);

    const morningEndHour = 11;
    const workdayMinHour = 7;
    const workdayMaxHour = 7;

    const blockedSubeventOptions = [
        { display: "Custom", value: "1" },
        { display: "Appointment Reservation", value: "2" },
        { display: "Field Work", value: "3" },
        { display: "Training", value: "4" },
        { display: "Meeting", value: "5" },
        { display: "Drive Time", value: "6" },
    ]
    const timeOffSubeventOptions = [
        { display: "Custom", value: "1" },
        { display: "Before Availability", value: "2" },
        { display: "After Availability", value: "3" },
        { display: "Lunch Break", value: "4" },
        { display: "Day Off", value: "5" },
        { display: "Overflow Scheduling", value: "6" },
        { display: "Sick Leave", value: "7" },
        { display: "Holiday", value: "8" },
    ]
    const [subeventOptions, setSubeventOptions] = useState(blockedSubeventOptions);

    const errorMessages = {
        enterRequiredFields: "Please enter all required fields",
        enterWorkHours: "Please enter a time during work hours (7 AM - 7 PM)",
        enterInstaller: "Please select at least one technician for this task",
        badJiraTicketId: "Jira tickets are formatted as CITY-XXXX (where Xs are numbers). Please re-enter with the correct ticket #.",
        badHermesTicketId: "Kairos tickets are formatted as CITY-XXXX (where Xs are numbers). Please re-enter with the correct ticket #.",
        completedRescheduleNotice: "Notice: This task has been marked as completed (or needs action). If this task needs to be rescheduled, please instead create a new task at the rescheduled date and time.",
        unavailableForSchedule: "An installer you have selected is no longer available for appointments. Please select another.",
        timeLockedNotice: "You do not have permission to change appointment times. Contact the Service Delivery team to reschedule."
    }

    const { showMessage } = useContext(MessageContext);
    const { account, tokenCache, roles } = useAuth();
    // create mode stays editable; creating is gated by the separate Create role
    const timeLocked = Boolean(activeEvent) && !roles?.includes(RESCHEDULE_ROLE);

    // Check for edit existing WO
    useEffect(() => {
        // Set fields
        if (activeEvent) {
            setTitle("Edit Work Order Task");

            // Set event type dropdown
            setEventType(activeEvent.ScheduleEventType.id);

            // Set status type dropdown. Only show on edit.
            setShowEventStatusType(true);
            setEventStatus(activeEvent.ScheduleEventStatus.id);

            if (activeEvent.ScheduleEventStatus.name === "Completed" || activeEvent.ScheduleEventStatus.name === "Needs Action" || activeEvent.ScheduleEventStatus.name === "Needs Remote Activation") {
                setShowFormError(true);
                setFormErrorMessage(errorMessages.completedRescheduleNotice);
            }

            // Set event date and time slot
            setJobDate(dayjs(activeEvent.datetimeStart).tz(defaultTz).format("YYYY-MM-DD"));
            let hour = dayjs(activeEvent.datetimeStart).tz(defaultTz).format("H");
            setJobHour(hour > 12 ? hour - 12 : hour);
            setJobMinute(dayjs(activeEvent.datetimeStart).tz(defaultTz).format("m"));

            let calculateDiff = dayjs(activeEvent.datetimeEnd)
            let diffMins = calculateDiff.diff(dayjs(activeEvent.datetimeStart), 'm');
            let diffHours = Math.floor(diffMins / 60);
            diffMins = diffMins - (diffHours * 60);
            setDurationHour(diffHours);
            if (diffMins === 0) {
                setDurationMinute(1);
            } else {
                setDurationMinute(diffMins);
            }

            // Set COS Work order num and related notes
            setWorkOrderNumber(activeEvent.cosWorkOrderNumber);
            if (activeEvent.ScheduleEventType.name === "Blocked Time") {
                setShowWorkOrderNumber(false);
            }

            setNotes(activeEvent.notes);

            // Set COS Work order num and related notes
            // setJiraTicketId(activeEvent.jiraTicketId);
            setHermesTicketId(activeEvent.hermesTicketId);

            if ((activeEvent.failedNotes && activeEvent.failedNotes.length) || activeEvent.ScheduleEventStatus.name === "Needs Action" || activeEvent.ScheduleEventStatus.name === "Needs Remote Activation") {
                setShowFailedNotes(true);
                setFailedNotes(activeEvent.failedNotes);
            }

            // If blocked time, show subevent dropdown/text box
            if (activeEvent.subEvent) {
                setShowSubevent(true);
                if (activeEvent.ScheduleEventType.name === "Blocked Time") {
                    setSubeventOptions(blockedSubeventOptions);
                    var sub = blockedSubeventOptions.find(s => {
                        return s.display === activeEvent.subEvent;
                    });
                    setShowWorkOrderNumber(false);
                } else if (activeEvent.ScheduleEventType.name === "Time Off") {
                    setSubeventOptions(timeOffSubeventOptions);
                    var sub = timeOffSubeventOptions.find(s => {
                        return s.display === activeEvent.subEvent;
                    });
                    setShowWorkOrderNumber(false);
                }

                if (sub === undefined) {
                    setShowCustomSubevent(true);
                    setCustomSubevent(activeEvent.subEvent);
                    setEventSubevent("1"); // Set dropdown to custom
                    setShowWorkOrderNumber(false);
                } else {
                    setEventSubevent(sub.value);
                }
            }

            // If multiple installers assigned to event, check them off
            multiInstaller.map((mi) => {
                if (activeEvent.Installers.some((i) => i.id === mi.id)) {
                    mi.isChecked = true;
                }
            });
            setMultiInstaller([...multiInstaller]);

            if (activeEvent.Installers.length === 1) {
                setInstaller(activeEvent.Installers[0].id);
            } else {
                setInstallerType("2");
            }

            // Check switch from morning to afternoon
            if (hour > morningEndHour) {
                setAmPm("2");
            }
        } else if (eventEditParams) {
            // TODO: Flesh out edit auto select
            setInstaller(eventEditParams.user.id);
            setJobDate(dayjs(eventEditParams.date).tz(defaultTz).format("YYYY-MM-DD"));
            setJobHour(eventEditParams.hour > 12 ? eventEditParams.hour - 12 : eventEditParams.hour);
            setJobMinute(eventEditParams.minute);
            if (eventEditParams.hour > morningEndHour) {
                setAmPm("2");
            }
        }

    }, [eventEditParams, activeEvent]);

    // Get event types and installers on form load
    useEffect(() => {
        async function getFormData() {
            const typesReq = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/schedule-event-types`, {
                method: 'GET',
                credentials: 'include' // Include credentials in cross-origin requests
            });
            const statusTypesReq = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/schedule-event-statuses`, {
                method: 'GET',
                credentials: 'include' // Include credentials in cross-origin requests
            });
            const installersReq = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/installers`, {
                method: 'GET',
                credentials: 'include' // Include credentials in cross-origin requests
            });

            // Handle errors consistently
            const typesErrorHandled = await handleSchedulingApiError(typesReq, showMessage);
            const statusTypesErrorHandled = await handleSchedulingApiError(statusTypesReq, showMessage);
            const installersErrorHandled = await handleSchedulingApiError(installersReq, showMessage);

            if (typesErrorHandled || statusTypesErrorHandled || installersErrorHandled) {
                return; // Stop execution if any error was handled
            }

            setEventTypes(await typesReq.json());
            setEventStatusTypes(await statusTypesReq.json());
            setInstallers(await installersReq.json());
        }

        getFormData();
    }, [ ]);

    // Set loading spinner icons
    useEffect(() => {
        if (isLoading) {
            setIsLoading(true);
        }

        return () => {
            setIsLoading(false);
        }
    }, [isLoading]);

    function handleScheduleEventTypeChange(e) {
        setEventType(e.target.value);
        var type = eventTypes.find((t) => t.id == e.target.value);
        setDurationHour(type.defaultDurationHours);

        if (e.target.textContent === "Blocked Time") {
            setSubeventOptions(blockedSubeventOptions);
            setShowSubevent(true);
            setShowWorkOrderNumber(false);
        } else if (e.target.textContent === "Time Off") {
            setSubeventOptions(timeOffSubeventOptions);
            setShowSubevent(true);
            setShowWorkOrderNumber(false);
        } else {
            setShowSubevent(false);
            setShowCustomSubevent(false);
            setShowWorkOrderNumber(true);
        }

        // TODO: Not sure why it doesn't autopopulate display if value is 0
        // Cheat this for now
        if (type.defaultDurationMinutes === 0) {
            setDurationMinute(1);
        } else {
            setDurationMinute(type.defaultDurationMinutes);
        }
    }

    function handleScheduleEventStatusTypeChange(e) {
        setEventStatus(e.target.value);

        if (e.target.textContent === "Needs Action" || e.target.textContent === "Needs Remote Activation") {
            setShowFailedNotes(true);
        } else {
            setShowFailedNotes(false);
        }
    }

    function handleSubeventChange(e) {
        setEventSubevent(e.target.value);
        if (e.target.value === "1") { // Checking for custom subevent. TODO: make this cleaner
            setShowCustomSubevent(true);
        } else {
            setShowCustomSubevent(false);
        }
    }


    function handleMultiChange(e) {
        if (installer !== undefined) {
            setInstaller(undefined);
        }
        multiInstaller.map((mi) => {
            if (mi.id == e.target.id) {
                mi.isChecked = !mi.isChecked;
            }
        });
        setMultiInstaller([...multiInstaller]);
    }


    function handleSetJobHour(e) {
        setJobHour(e.target.value);
        if (jobMinute === undefined) {
            setJobMinute("0"); // Set to the top of the hour if not already defined
        }
    }

    // Real-time availability checking - triggers whenever date/time/duration/installer changes
    useEffect(() => {
        // Only check if all required fields are filled
        if (!jobDate || !jobHour || jobMinute === undefined || !durationHour || !durationMinute) {
            setAvailabilityConflict(null);
            return;
        }

        // Get installer IDs
        let installerIds = [];
        if (installerType === "1" && installer !== undefined && installer !== "") {
            installerIds = [installer];
        } else if (installerType === "2") {
            installerIds = multiInstaller.filter(mi => mi.isChecked).map(mi => mi.id);
        }

        // Need at least one installer selected
        if (installerIds.length === 0) {
            setAvailabilityConflict(null);
            return;
        }

        // Calculate start and end times
        let formattedDate = jobDate + ` ${ampm === "1" || parseInt(jobHour) === 12 ? parseInt(jobHour) : parseInt(jobHour) + 12}:${jobMinute}`;
        let start = dayjs.tz(formattedDate, defaultTz);
        let min = durationMinute === 1 ? 0 : durationMinute; // Handle the weird 1 = 0 hack
        let end = start.clone().add(parseInt(durationHour) * 60 + parseInt(min), 'minutes');

        // Debounce the API call to avoid too many requests
        const timeoutId = setTimeout(async () => {
            setCheckingAvailability(true);
            setAvailabilityConflict(null); 
                        
            try {
                const req = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/check-availability`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        installerIds: installerIds,
                        datetimeStart: start.toISOString(),
                        datetimeEnd: end.toISOString(),
                        excludeEventId: activeEvent?.id // Don't count current event as conflict when editing
                    })
                });

                const errorHandled = await handleSchedulingApiError(req, showMessage);
                if (errorHandled) {
                    setCheckingAvailability(false);
                    return;
                }

                const result = await req.json();
                
                if (result.hasConflict) {
                    // Build conflict message
                    const conflictDetails = result.conflicts.map(conflict => {
                        const installerNames = conflict.Installers.map(i => 
                            `${i.User.firstName} ${i.User.lastName}`
                        ).join(', ');
                        const conflictStart = dayjs(conflict.datetimeStart).tz(defaultTz).format("h:mm A");
                        const conflictEnd = dayjs(conflict.datetimeEnd).tz(defaultTz).format("h:mm A");
                        return `${installerNames}: ${conflictStart} - ${conflictEnd} (${conflict.ScheduleEventType.name})`;
                    }).join('\n');
                    
                    setAvailabilityConflict(conflictDetails);
                } else {
                    setAvailabilityConflict(null);
                }
            } catch (error) {
                console.error("Error checking availability:", error);
            } finally {
                setCheckingAvailability(false);
            }
        }, 500); // 500ms debounce delay

        // Cleanup timeout on dependency change
        return () => clearTimeout(timeoutId);
    }, [jobDate, jobHour, jobMinute, ampm, durationHour, durationMinute, installer, installerType, multiInstaller, activeEvent]);


    async function submitForm() {
        setShowFormError(false);

        // Check required fields
        if (![eventType, jobDate, jobHour, jobMinute].every(Boolean)) {
            setShowFormError(true);
            setFormErrorMessage(errorMessages.enterRequiredFields)
            return;
        }

        // TODO: Fix this dumb hack
        if (jobMinute === 1) {
            jobMinute = 0;
        }
        let min = durationMinute;
        if (min === 1) {
            min = 0;
        }
        let data = {};

        // skipped when locked: the time is resent unchanged and the user could not correct it anyway
        if (!timeLocked && ((ampm === "1" && parseInt(jobHour) < workdayMinHour) || ((ampm === "2" && parseInt(jobHour) > workdayMaxHour) && parseInt(jobHour) !== 12))) {
            setShowFormError(true);
            setFormErrorMessage(errorMessages.enterWorkHours);
            return;
        }

        let formattedDate = jobDate + ` ${ampm === "1" || parseInt(jobHour) === 12 ? parseInt(jobHour) : parseInt(jobHour) + 12}:${jobMinute}`;

        // Set start time
        let start = dayjs.tz(formattedDate, defaultTz);

        // Clone start time, add job duration to calculate end time
        let end = start.clone();
        end = end.add(parseInt(durationHour)*60 + parseInt(min), 'minutes');

        if (installer !== undefined) {
            data["installerIds"] = [installer];
        } else {
            let ids = [];
            multiInstaller.map((mi) => {
                if (mi.isChecked) {
                    ids.push(mi.id);
                }
            });
            data["installerIds"] = ids;
        }

        if (!data["installerIds"].length) { // Check for single installer selection and multi installer selection
            setShowFormError(true);
            setFormErrorMessage(errorMessages.enterInstaller)
            return;
        } else if (data["installerIds"].length && !data["installerIds"][0]) {
            setShowFormError(true);
            setFormErrorMessage(errorMessages.enterInstaller)
            return;
        }

        let subEvent = "";
        if (eventSubevent !== undefined) {
            let sub = subeventOptions.find(s => {
                return s.value === eventSubevent;
            });

            if (sub.display === "Custom") {
                subEvent = customSubevent;
            } else {
                subEvent = sub.display;
            }
        }

        // if (jiraTicketId !== undefined && jiraTicketId !== null && jiraTicketId.length) {
        //     const jiraTicketRegex = /^CITY-\d+$/;
        //     if (!jiraTicketRegex.test(jiraTicketId)) {
        //         setShowFormError(true);
        //         setFormErrorMessage(errorMessages.badJiraTicketId);
        //         return;
        //     }
        // }


        if (hermesTicketId !== undefined && hermesTicketId !== null && hermesTicketId.length) {
            const hermesTicketRegex = /^CITY-\d+$/;
            if (!hermesTicketRegex.test(hermesTicketId)) {
                setShowFormError(true);
                setFormErrorMessage(errorMessages.badHermesTicketId);
                return;
            }
        }

         // TODO: get multi installers
        // resend the stored strings verbatim; rebuilding from the dropdowns can drift and trip the backend reschedule guard
        data["datetimeStart"] = timeLocked ? activeEvent.datetimeStart : start.toISOString();
        data["datetimeEnd"] = timeLocked ? activeEvent.datetimeEnd : end.toISOString();
        data["notes"] = notes;
        data["failedNotes"] = failedNotes;
        data["subEvent"] = subEvent;
        data["cosWorkOrderNumber"] = workOrderNumber;
        // data["jiraTicketId"] = jiraTicketId;
        data["hermesTicketId"] = hermesTicketId; // Temporary until backend standardizes on one field
        data["scheduleEventTypeId"] = parseInt(eventType);
        data["scheduleEventStatusId"] = parseInt(eventStatus);
        data["account"] = account;
        data["tokenCache"] = tokenCache;

        setIsLoading(true);
        let req = undefined;

        if (activeEvent) {
            data["id"] = activeEvent.id;
            req = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/schedule-event`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                credentials: 'include', // Include credentials in cross-origin requests
                body: JSON.stringify(data)
            });
        } else {
            req = await fetch(`${process.env.REACT_APP_API_URL}/scheduling/schedule-event`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                credentials: 'include', // Include credentials in cross-origin requests
                body: JSON.stringify(data)
            });
        }

        // TODO: Show error if things break, don't close out form.
        const res = await req;
        setIsLoading(false);

        // Handle errors consistently
        const errorHandled = await handleSchedulingApiError(req, showMessage);
        if (errorHandled) {
            setIsLoading(false);
            return;
        }

        if (res.status === 500) {
            setShowServerError(true);
            return;
        }
        closeEventModal();
    }

    return (
        <ModalWrapper
            title = { title }
            onClose = { closeEventModal }
            onSubmit = { submitForm }
        >
            <div className="flex flex-col gap-3 p-2 lg:p-4 w-full overflow-auto overflow-y-auto bg-light-10">
                {/* Work order type radio buttons & Dropdown */}
                <Dropdown
                    // TODO: Select different types, change form based on that
                    label = {
                        <div className="flex gap-2 pb-[1px] font-semibold">
                            Work Order Task
                            {/* <RadioGroup
                                value = { jobType }
                                small = { true }
                                horizontal = { true }
                                groupName = "eventGroup"
                                onChange= { (value) => changeWorkOrderType(value) }
                                options = {[
                                    { label: "Customer", value: "1" },
                                    { label: "Other", value: "2" },
                                ]}
                            />   */}
                        </div>
                    }
                    className = "w-full"
                    value = { eventType }
                    onChange = { handleScheduleEventTypeChange }
                    placeholder = "Select a Work Order Task"
                    options = { eventTypes.map((e) => {
                        return {
                            display: e.name,
                            value: e.id,
                            icon: eventIcons[e.icon]?.icon,
                        }
                    })}
                />

                {/* Work order type radio buttons & Dropdown */}
                {showEventStatusType &&
                    <Dropdown
                        // TODO: Select different types, change form based on that
                        label = {
                            <div className="flex gap-2 pb-[1px] font-semibold">
                                Current Status
                            </div>
                        }
                        className = "w-full"
                        value = { eventStatus }
                        onChange = { handleScheduleEventStatusTypeChange }
                        placeholder = "Select the Work Order Task Status"
                        options = { eventStatusTypes.map((s) => {
                            return {
                                display: s.name,
                                value: s.id,
                            }
                        })}
                    />
                }

                {/* Custom subevent textarea */}
                {showSubevent &&
                    <Dropdown
                        // TODO: Select different types, change form based on that
                        label = "Subtask"
                        className = "w-full"
                        value = { eventSubevent }
                        onChange = { handleSubeventChange }
                        placeholder = "Select a Subtask For Tech's Blocked Time"
                        options = { subeventOptions }
                    />
                }


                {/* Custom subevent textarea */}
                {showCustomSubevent &&
                    <Textarea
                        label = "Custom Subtask"
                        rows = "1"
                        placeholder = "Type custom subtask here (Sick leave, Lunch Time, Field Work, etc.)..."
                        value = { customSubevent }
                        onChange = { (e) => setCustomSubevent(e.target.value) }
                    />
                }

                {/* Installer radio buttons & Dropdown */}
                <Dropdown
                    label = {
                        <div className="flex gap-2 pb-[1px] font-semibold">
                            Installer:
                            <RadioGroup
                                value = { installerType }
                                small = { true }
                                horizontal = { true }
                                groupName = "installerGroup"
                                onChange = {(value) => {
                                        if (value === "2") {
                                            setInstaller("");
                                        }
                                        setInstallerType(value)
                                    }
                                }
                                options = {[
                                    { label: "Single", value: "1" },
                                    { label: "Multiple", value: "2" },
                                ]}
                            />
                        </div>
                    }
                    className = "w-full"
                    value = { installer }
                    onChange = {(e) => {
                            setInstaller(e.target.value);
                        }
                    }
                    options = { installers.map((i) => {
                        const displaySuffix = i.InstallerType.name === "Region" ? "" : " Tech";
                        return {
                            key: i.id,
                            value: i.id,
                            display: `${i.User.firstName} ${i.User.lastName} (${i.InstallerType.name}${displaySuffix})`,
                        }
                    })}
                    disabled = { installerType === "2" }
                    />
                    {/* Multi installer select */ }
                    { installerType === "2" &&
                        <div className="flex flex-col sm:flex-wrap sm:h-[32rem] min-h-[32rem] -mt-2 gap-3 border border-light-2 rounded p-2 overflow-y-auto">
                            { multiInstaller.map((e) => (
                                <Checkbox
                                    label = { e.display }
                                    checked = { e.isChecked }
                                    onChange = { handleMultiChange }
                                    id = { e.id }
                                    key = { e.id }
                                />
                            ))}
                        </div>
                    }
                    {/* Date select */}
                {
                    <div className="flex flex-row flex-wrap gap-4">
                        {/* Calendar date picker */}
                        <Input
                            type = "date"
                            label = "Date"
                            className = "w-full sm:w-60"
                            disabled = { timeLocked }
                            value = { jobDate }
                            onChange = {(e) => {
                                setJobDate(e.target.value)
                            }}
                        />
                        {/* Start time dropdowns */}
                        <div className="flex flex-row w-full sm:w-auto gap-1 items-end">
                            <Dropdown
                                label = "Start Time"
                                className = "w-1/2 sm:w-20"
                                classList = "justify-center"
                                placeholder = "Hrs"
                                readOnly = { timeLocked }
                                value = { jobHour }
                                onChange = {(e) => {
                                    handleSetJobHour(e)
                                }}
                                options = {[
                                    { display: "1", value: 1 },
                                    { display: "2", value: 2 },
                                    { display: "3", value: 3 },
                                    { display: "4", value: 4 },
                                    { display: "5", value: 5 },
                                    { display: "6", value: 6 },
                                    { display: "7", value: 7 },
                                    { display: "8", value: 8 },
                                    { display: "9", value: 9 },
                                    { display: "10", value: 10 },
                                    { display: "11", value: 11 },
                                    { display: "12", value: 12 },
                                ]}
                            />
                            <div className="pb-2 -mx-[2px] place-self-end">:</div>
                            <Dropdown
                                className = "w-1/2 sm:w-20"
                                classList = "justify-center"
                                placeholder = "Mins"
                                readOnly = { timeLocked }
                                value = { jobMinute }
                                onChange = {(e) => {
                                    setJobMinute(e.target.value)
                                }}
                                options = {[
                                    { display: "00", value: 0 },
                                    { display: "15", value: 15 },
                                    { display: "30", value: 30 },
                                    { display: "45", value: 45 },
                                ]}
                            />
                            <Dropdown
                                classList = "justify-center"
                                readOnly = { timeLocked }
                                value = { ampm }
                                onChange = {(e) => {
                                    setAmPm(e.target.value)
                                }}
                                options = {[
                                    { display: "AM", value: "1" },
                                    { display: "PM", value: "2" },
                                ]}
                            />
                        </div>

                        {/*   duration */}
                        <div className="flex gap-1 ml-auto w-full lg:w-auto">
                            <Dropdown
                                label= { <div className="flex justify-between items-baseline"><p>Duration</p><p className="text-sm">Hours</p></div> }
                                className = "w-full sm:w-32"
                                classList = "justify-center"
                                readOnly = { timeLocked }
                                value = { durationHour }
                                onChange = {(e) => {
                                    setDurationHour(e.target.value)
                                }}
                                options = {[
                                    { display: "0", value: 0 },
                                    { display: "1", value: 1 },
                                    { display: "2", value: 2 },
                                    { display: "3", value: 3 },
                                    { display: "4", value: 4 },
                                    { display: "5", value: 5 },
                                    { display: "6", value: 6 },
                                    { display: "7", value: 7 },
                                    { display: "8", value: 8 },
                                    { display: "9", value: 9 },
                                    { display: "10", value: 10 },
                                    { display: "11", value: 11 },
                                    { display: "12", value: 12 },
                                ]}
                            />
                            <Dropdown
                                label={ <div className="flex items-baseline"><p className="ml-auto text-sm">Minutes</p></div> }
                                className = "w-full sm:w-32"
                                classList = "justify-center"
                                readOnly = { timeLocked }
                                value = { durationMinute }
                                onChange = {(e) => {
                                    setDurationMinute(e.target.value)
                                }}
                                options = {[
                                    { display: "00", value: 1 },
                                    { display: "15", value: 15 },
                                    { display: "30", value: 30 },
                                    { display: "45", value: 45 },
                                ]}
                            />
                        </div>

                        {/* Reschedule lock notice */}
                        {timeLocked && (
                            <div className="w-full bg-blue-100 border border-blue-400 text-blue-700 px-4 py-2 rounded text-sm" role="note">
                                {errorMessages.timeLockedNotice}
                            </div>
                        )}

                        {/* Availability conflict warning */}
                        {availabilityConflict && !checkingAvailability && (
                            <div className="bg-orange-100 border border-orange-400 text-orange-800 px-4 py-3 rounded relative" role="alert">
                                <strong className="font-bold">⚠️ Schedule Conflict Detected</strong>
                                <div className="mt-2 text-sm whitespace-pre-line">{availabilityConflict}</div>
                                <div className="mt-2 text-sm">Please adjust the appointment window to avoid conflict, or receive permission from your supervisor to allow the appointment overlap before submitting.</div>
                            </div>
                        )}

                        {/* Work order # textarea */}
                        {showWorkOrderNumber &&
                            <Textarea
                                label = "(COS) Work Order #"
                                rows = "1"
                                placeholder = "Type [required] work order # here (WOXXXXXXX)..."
                                value = { workOrderNumber }
                                onChange = { (e) => setWorkOrderNumber(e.target.value) }
                            />
                        }

                        {/* <Textarea
                            label = "(Jira) Ticket #"
                            rows = "1"
                            placeholder = "Type [optional] ticket # here (CITY-XXXX)..."
                            value = { jiraTicketId }
                            onChange = { (e) => setJiraTicketId(e.target.value) }
                        /> */}

                        <Textarea
                            label = "(Kairos) Ticket #"
                            rows = "1"
                            placeholder = "Type [optional] ticket # here (CITY-XXXX)..."
                            value = { hermesTicketId }
                            onChange = { (e) => setHermesTicketId(e.target.value) }
                        />

                        {/* Misc notes textarea */}
                        <Textarea
                            label = "Task Notes"
                            rows = "10"
                            placeholder = "Type any additional notes here..."
                            value = { notes }
                            onChange = { (e) => setNotes(e.target.value) }
                        />

                        {/* Failed notes textarea */}
                        {showFailedNotes &&
                            <Textarea
                                label = "Needs Action Notes"
                                rows = "10"
                                placeholder = "Type what requires further action here..."
                                value = { failedNotes }
                                onChange = { (e) => setFailedNotes(e.target.value) }
                            />
                        }
                    </div>
                }

                {/* Form error messages */}
                {showFormError &&
                    <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative" role="alert">
                        <strong className="font-bold">{formErrorMessage}</strong>
                    </div>
                }
                {showServerError &&
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <strong className="font-bold">Something went wrong...&nbsp;</strong>
                        <span className="block sm:inline">Please confirm that the COS WO # and Object # are entered correctly. If additional attempts are unsuccessful, please report the issue to devs@citysidefiber.com...</span>
                    </div>
                }
            </div>
        </ModalWrapper>
    );
}
